<template>
  <b-modal
    id="modal-to-sort-new-view"
    ref="modal"
    v-model="open"
    hide-footer
    no-close-on-backdrop
    centered
    size="lg"
    :title="'To Be Sort New - ' + bucketName"
  >
    <b-overlay
      id="overlay-background"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <b-table
        striped
        hover
        responsive
        :per-page="perPage"
        :items="items"
        :fields="fields"
        :json_fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        sticky-header="100vh"
      >
        <template #empty>
          <TableDataFetching
            :rows="items"
            :data-loading="dataLoading"
          />
        </template>
        <template #cell(waybill_number)="data">
          <div class="d-flex align-items-center">
            <div
              class="font-weight-bolder ml-1 text-primary"
              @click="onClickViewOrder(data.item.id)"
            >
              {{ data.value }}
            </div>
            <div
              class="cursor-pointer text-secondary ml-1"
              @click="copyToClipboard(data.item.waybill_number)"
            >
              <feather-icon
                class="align-middle"
                icon="CopyIcon"
                size="16"
              />
            </div>
          </div>
        </template>
        <template #cell(status)="data">
          <div class="d-flex justify-content-center">
            <order-status-badge :status-key="data.item.latest_primary_status_key" />
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row
          cols="12"
        >
          <b-col
            class="d-flex align-items-center justify-content-sm-start"
            md="6"
          >
            <label class="width-75">Per page</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block width-125"
            />
          </b-col>
          <!-- Pagination -->
          <b-col
            class="d-flex align-items-center justify-content-sm-end"
            md="6"
          >
            <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="(meta.total)? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-overlay>
    <view-order-modal ref="viewOrderModal" />
  </b-modal>
</template>

<script>
import {
  BModal,
  BCol,
  BRow,
  BOverlay,
  BTable,
  VBModal,
  BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import OrderStatusBadge from '@/@core/components/status/OrderStatusBadge.vue'

const sortingRepository = RepositoryFactory.get('sorting')

function initialState() {
  return {
    open: false,
    bucketId: null,
    bucketName: '',
    modalLoading: false,
    items: [],
    dataLoading: false,
    perPage: 20,
    pageOptions: [20, 50, 100, 200],
    page: 1,
    meta: {
      from: 0,
      to: 0,
      total: 0,
    },
    fields: [
      {
        key: 'waybill_number',
        label: 'Waybill Number',
        sortable: true,
        tdClass: 'text-left',
      },
      {
        key: 'status',
        label: 'Status',
        thClass: 'text-center',
      },
    ],
    sortBy: '',
    sortDesc: false,
    sortDirection: 'asc',
    loadingResponse: false,
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BModal,
    BCol,
    BRow,
    BOverlay,
    BTable,
    BPagination,
    ViewOrderModal,
    OrderStatusBadge,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  watch: {
    page() {
      this.fetchToBeSortNewOrders()
    },
    perPage() {
      this.fetchToBeSortNewOrders()
    },
  },
  methods: {
    async openModal(bucketId, bucketName) {
      Object.assign(this.$data, initialState())
      this.bucketId = bucketId
      this.bucketName = bucketName
      this.open = true
      this.fetchToBeSortNewOrders()
    },
    async fetchToBeSortNewOrders() {
      if (!this.bucketId) return
      this.dataLoading = true
      this.items = []
      try {
        const { data } = await sortingRepository.getToBeSortNewOrders(this.page, this.perPage, this.bucketId)
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async copyToClipboard(waybill) {
      try {
      // Use the browser Clipboard API
        await navigator.clipboard.writeText(waybill)

        // Show success notification
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CopyIcon',
            variant: 'success',
            text: `Waybill ${waybill} copied to clipboard!`,
          },
        })
      } catch (err) {
        this.showErrorMessage('Failed to copy text')
      }
    },
    async onClickViewOrder(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../@core/scss/vue/libs/vue-select.scss';
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.custom-button-color-blur {
  background-color: #6da5ff !important;
  border-color: #6da5ff !important;
}
.cursor-pointer:hover {
  opacity: 0.7;
  transform: scale(1.1);
  transition: all 0.2s ease-in-out;
}
</style>
